import React, { memo, useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import styles from '../../../assets/styles/pageStyles/ruleBasedInbox/threadView/threadViewSkeleton.module.scss';
import { useRuleBasedInboxStore } from '../../../store/ruleBasedInboxStore';
import { THREAD_CHANNEL_NAMES } from '../constants';

// Color constants for skeleton backgrounds (matching SCSS variables)
const SKELETON_COLORS = {
    BOT_MESSAGE: '#ffffff',
    USER_MESSAGE: '#dcf8c6',
    DEFAULT_MESSAGE:'rgb(236, 236, 236)',
} as const;

interface MessageSkeletonProps {
    variant: 'bot' | 'user';
    width: string;
    height: number;
    isWhatsAppChannel: boolean;
}

/**
 * Individual message skeleton - reusable for bot and user messages
 */
const MessageSkeleton: React.FC<MessageSkeletonProps> = memo(({ variant, width, height, isWhatsAppChannel }) => {
    const isBot = variant === 'bot';
    const bgColor = isWhatsAppChannel
        ? (isBot ? SKELETON_COLORS.BOT_MESSAGE : SKELETON_COLORS.USER_MESSAGE)
        : SKELETON_COLORS.DEFAULT_MESSAGE;

    return (
        <div className={`${styles.messageRow} ${isBot ? styles.messageRowBot : styles.messageRowUser}`}>
            {isBot && (
                <Skeleton
                    variant="circular"
                    width={28}
                    height={28}
                    className={styles.avatarSkeleton}
                />
            )}
            <Skeleton
                variant="rounded"
                width={width}
                height={height}
                sx={{ bgcolor: bgColor }}
            />
        </div>
    );
});

MessageSkeleton.displayName = 'MessageSkeleton';

// Predefined message patterns for realistic skeleton layout
const MESSAGE_PATTERNS: Omit<MessageSkeletonProps, 'isWhatsAppChannel'>[] = [
    { variant: 'bot', width: '40%', height: 50 },
    { variant: 'user', width: '45%', height: 40 },
    { variant: 'bot', width: '55%', height: 65 },
    { variant: 'user', width: '35%', height: 45 },
    { variant: 'bot', width: '30%', height: 35 },
    { variant: 'user', width: '50%', height: 55 },
    { variant: 'bot', width: '45%', height: 40 },
];

interface ThreadViewSkeletonProps {
    /** Height of the skeleton container in pixels */
    height?: number | string;
}

/**
 * Resolves channel_name for the currently loading thread.
 * Prefers the matching entry in ruleBasedInboxStore.threads; falls back to
 * currentActiveChatbotThread when the thread is not yet in the list.
 */
const useLoadingThreadChannelName = (): string | null => {
    const { threads, selectedThreadId, currentActiveChatbotThread } = useRuleBasedInboxStore(
        useShallow((state) => ({
            threads: state.threads,
            selectedThreadId: state.selectedThreadId,
            currentActiveChatbotThread: state.currentActiveChatbotThread
        }))
    );

    return useMemo(() => {
        const threadFromList = threads.find((thread) => thread.thread_id === selectedThreadId);
        if (threadFromList?.channel_name) {
            return threadFromList.channel_name;
        }

        if (currentActiveChatbotThread && currentActiveChatbotThread.channel_name) {
            return currentActiveChatbotThread.channel_name;
        }

        // Old thread shape may still expose route source as the channel identifier
        if (currentActiveChatbotThread?.chatbot_user_route_source) {
            return currentActiveChatbotThread.chatbot_user_route_source;
        }

        return null;
    }, [threads, selectedThreadId, currentActiveChatbotThread]);
};

/**
 * Thread View Skeleton Component
 * Displays a loading skeleton for the chat thread view in Rule-Based Inbox.
 * WhatsApp keeps the beige chat canvas + green/white bubbles; other channels
 * use a white canvas with uniform grey message placeholders.
 */
const ThreadViewSkeleton: React.FC<ThreadViewSkeletonProps> = memo(({ height = '100%' }) => {
    const channelName = useLoadingThreadChannelName();
    // Default to custom skeleton when channel is unknown (e.g. page refresh before data loads)
    const isWhatsAppChannel =
        Boolean(channelName) &&
        channelName?.toLowerCase() === THREAD_CHANNEL_NAMES['WHATSAPP'].toLowerCase();

    return (
        <Box className={styles.skeletonContainer} sx={{ height }}>
            {/* Header */}
            <div className={styles.header}>
                <Skeleton variant="circular" width={36} height={36} />
                <div className={styles.headerInfo}>
                    <Skeleton variant="text" width={150} height={22} />
                    <Skeleton variant="text" width={100} height={14} />
                </div>
                <div className={styles.headerActions}>
                    <Skeleton variant="rounded" width={75} height={30} />
                    <Skeleton variant="rounded" width={75} height={30} />
                    <Skeleton variant="circular" width={24} height={24} />
                </div>
            </div>

            {/* Messages Area */}
            <div
                className={`${styles.messagesArea} ${!isWhatsAppChannel ? styles.messagesAreaDefault : ''}`}
            >
                <div className={styles.messagesContainer}>
                    {MESSAGE_PATTERNS.map((pattern, index) => (
                        <MessageSkeleton
                            key={index}
                            variant={pattern.variant}
                            width={pattern.width}
                            height={pattern.height}
                            isWhatsAppChannel={isWhatsAppChannel}
                        />
                    ))}
                </div>
            </div>

            {/* Input Area */}
            <div className={styles.inputArea}>
                {/* <Skeleton variant="circular" width={36} height={36} /> */}
                <Skeleton variant="rounded" height={40} className={styles.inputField} />
                <Skeleton variant="circular" width={36} height={36} />
            </div>
        </Box>
    );
});

ThreadViewSkeleton.displayName = 'ThreadViewSkeleton';

export default ThreadViewSkeleton;
